import {
  isRuteroOnRequestStatus,
  parseRuteroPassengerFieldRef,
  sanitizeRuteroConfirmBlock,
} from '@bandido/shared/types';
import type {
  RuteroConfirmBlock,
  RuteroDeclarablePassengerField,
  RuteroPassengerFieldRef,
  RuteroPassengerInfoScope,
} from '@bandido/shared/types';

/**
 * Cómo se LEE el estado de una reserva.
 *
 * Los estados del hub son cuatro (`quoted`, `held`, `confirmed`, `cancelled`) y
 * a veces se quedan cortos: el proveedor agrega un matiz que el estado
 * unificado no captura, y lo publica en `providerStatus`. El caso que importa
 * es el ON REQUEST: VPT y AgentCars devuelven `confirmed` con
 * `providerStatus: "onrequest"` — la reserva EXISTE, pero el operador todavía
 * no la aceptó.
 *
 * Mostrar eso como "confirmado" es lo peor que podemos hacer: el asesor le dice
 * al pasajero que ya está, y el operador puede rechazarla. La regla es
 * mecánica: `providerStatus` presente ⇒ el estado unificado lleva un asterisco.
 */

/** Estados del hub, tal como llegan en el booking. */
export type BookingState = 'draft' | 'quoted' | 'held' | 'confirmed' | 'cancelled' | 'failed';

export type ActiveOperation = 'confirm' | 'cancel';

export type ActiveOperationStatus =
  | 'in_progress'
  | 'ambiguous'
  | 'reconciling'
  /** Dejó de reintentarse solo: necesita que alguien la mire. */
  | 'stalled';

export interface BookingActionState {
  state: BookingState;
  unknownProviderState?: boolean;
  activeOperation?: ActiveOperation;
  activeOperationStatus?: ActiveOperationStatus;
  activeOperationRecheckAt?: string;
  confirmBlock?: unknown;
  expiresAt?: string | null;
}

export interface ConfirmBlockPassengerField {
  ref: RuteroPassengerFieldRef;
  scope: RuteroPassengerInfoScope;
  field: RuteroDeclarablePassengerField;
}

/** Solo un bloqueo válido de una reserva todavía held conserva autoridad. */
export function bookingConfirmBlock(
  booking: BookingActionState,
): RuteroConfirmBlock {
  if (booking.state !== 'held') return null;
  return sanitizeRuteroConfirmBlock(booking.confirmBlock);
}

/** Presentación segura: nunca devuelve refs fuera del vocabulario compartido. */
export function confirmBlockPassengerFields(
  booking: BookingActionState,
): ConfirmBlockPassengerField[] {
  const block = bookingConfirmBlock(booking);
  if (!block) return [];
  return block.missingFields.flatMap((ref) => {
    const parsed = parseRuteroPassengerFieldRef(ref);
    return parsed ? [{ ref, ...parsed }] : [];
  });
}

/**
 * ¿El proveedor todavía no aceptó la reserva? La lista de formas en las que
 * los proveedores escriben "on request" vive en `@bandido/shared` y es la
 * MISMA que usa el backend para decidir si la reserva entra a la pestaña
 * "necesitan atención" del listado: con una copia de cada lado, la cola de
 * trabajo podría llenarse de reservas que esta pantalla pinta como
 * confirmadas normales (§21.22).
 */
export function isOnRequest(providerStatus?: string): boolean {
  return isRuteroOnRequestStatus(providerStatus);
}

/**
 * La clave i18n con la que se rotula el estado. Un `confirmed` on-request se
 * lee "pendiente de confirmación del operador" y no "confirmado"; el resto de
 * los estados no cambian.
 */
export function bookingStateLabelKey(
  state: string,
  providerStatus?: string,
): string {
  if (state === 'confirmed' && isOnRequest(providerStatus)) {
    return 'travelServices.bookingStates.onRequest';
  }
  return `travelServices.bookingStates.${state}`;
}

/**
 * Color del chip. El on-request es `warning` y no `success`: verde significa
 * cerrado, y esto todavía puede caerse.
 */
export function bookingStateColor(
  state: string,
  providerStatus: string | undefined,
  colors: Record<string, ChipColor>,
  fallback: ChipColor = 'default',
): ChipColor {
  if (state === 'confirmed' && isOnRequest(providerStatus)) return 'warning';
  return colors[state] ?? fallback;
}

export function hasActiveOperation(booking: BookingActionState): boolean {
  return booking.activeOperationStatus != null;
}

export function canConfirmBooking(booking: BookingActionState): boolean {
  return (
    booking.state === 'held' &&
    !(booking.expiresAt && Date.parse(booking.expiresAt) <= Date.now()) &&
    booking.unknownProviderState !== true &&
    bookingConfirmBlock(booking) === null &&
    !hasActiveOperation(booking)
  );
}

export function canCancelBooking(booking: BookingActionState): boolean {
  return (
    (booking.state === 'held' || booking.state === 'confirmed') &&
    booking.unknownProviderState !== true &&
    !hasActiveOperation(booking)
  );
}

export function canSyncActiveOperation(booking: BookingActionState): boolean {
  return (
    booking.activeOperationStatus === 'ambiguous' ||
    booking.activeOperationStatus === 'reconciling' ||
    // Estancada: el sistema dejó de reintentar, así que sincronizar a mano es
    // justamente la palanca que le queda al asesor.
    booking.activeOperationStatus === 'stalled'
  );
}

export function activeOperationLabelKey(
  booking: BookingActionState,
): string | undefined {
  if (!booking.activeOperation || !booking.activeOperationStatus) return undefined;
  return `travelServices.booking.activeOperation.${booking.activeOperation}.${booking.activeOperationStatus}`;
}

export function activeOperationSeverity(
  status?: ActiveOperationStatus,
): 'info' | 'warning' | 'error' {
  // Estancada no es una advertencia más: el sistema ya no va a resolverla y
  // puede haber una reserva viva en el proveedor.
  if (status === 'stalled') return 'error';
  return status === 'in_progress' ? 'info' : 'warning';
}

type ChipColor = 'default' | 'info' | 'success' | 'warning' | 'error';
